import { useCallback, useMemo } from 'react';
import { useSessionStorage } from 'react-use';
import useSWRMutation from 'swr/mutation';
import { useTellerSession } from '~/common/hooks/use-auth/use-teller-session';
import { sendToFirebaseAnalytics } from '~/common/hooks/use-firebase-analytics';
import { likeStory } from '@app/_repositories/like-service';

const LIKE_COUNT_KEY_PREFIX = 'lk-c-';

type Params = {
  storyId: string;
  onSuccess?: () => void;
  onShowShouldLogin?: () => void;
  onShowShouldInstallApp?: () => void;
};

export const useLike = ({
  storyId,
  onSuccess,
  onShowShouldLogin,
  onShowShouldInstallApp,
}: Params) => {
  const tellerSession = useTellerSession();
  const [likedCount, setLikedCount] = useSessionStorage(
    `${LIKE_COUNT_KEY_PREFIX}${storyId}`,
    0
  );

  const handleOnSuccess = useCallback(() => {
    const nextLikeCount = likedCount + 1;
    setLikedCount(nextLikeCount);
    onSuccess?.();

    // sent event to firebase analytics
    sendToFirebaseAnalytics('story_like', {
      likeCount: nextLikeCount,
    });
  }, [likedCount, onSuccess, setLikedCount]);

  const { trigger } = useSWRMutation(
    ['episode-like', storyId],
    ([_, storyId]) => likeStory(storyId),
    {
      onSuccess: handleOnSuccess,
    }
  );

  const enableLike = useMemo(() => {
    if (!tellerSession.session) return false;

    if (tellerSession.session.isAnonymous && likedCount > 0) {
      return false;
    }

    if (likedCount >= 10) {
      return false;
    }

    return true;
  }, [likedCount, tellerSession.session]);

  const sendLike = useCallback(async () => {
    if (!tellerSession.session) return;

    // If the user is not logged in and has liked the story, show the login modal
    if (tellerSession.session.isAnonymous && likedCount > 0) {
      onShowShouldLogin?.();
      return;
    }

    if (likedCount >= 10) {
      onShowShouldInstallApp?.();
      return;
    }

    await trigger();
  }, [
    tellerSession,
    likedCount,
    trigger,
    onShowShouldLogin,
    onShowShouldInstallApp,
  ]);

  return {
    sendLike,
    enableLike,
  };
};
